<script setup lang="ts">
const props = defineProps<{
  segments: number;
  colors: string[];
  labels?: string[];
  modelValue?: number | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | null): void;
}>();

function circleSegment(index: number, total: number) {
  let a1 = (index / total) * Math.PI * 2;
  let a2 = ((index + 1) / total) * Math.PI * 2;

  let x1 = Math.sin(a1);
  let x2 = Math.sin(a2);

  let y1 = -Math.cos(a1);
  let y2 = -Math.cos(a2);

  let radius = 37; // Increased radius
  let innerRadius = 5; // Increased inner radius

  let x = 50;
  let y = 50;

  return `
    M ${x + x1 * radius} ${y + y1 * radius}
    A ${radius} ${radius} 0 0 1 ${x + x2 * radius} ${y + y2 * radius}
    L ${x + x2 * innerRadius} ${y + y2 * innerRadius}
    A ${innerRadius} ${innerRadius} 0 0 0 ${x + x1 * innerRadius} ${y + y1 * innerRadius}
    L ${x + x1 * radius} ${y + y1 * radius}
  `;
}

async function delay(d: number) {
  await new Promise(r => setTimeout(r, d));
}
async function onSliceClick(index: number) {
  if (props.modelValue === undefined) {
    return;
  }

  if (props.modelValue === index) {
    emit('update:modelValue', null);
  } else if (props.modelValue === null) {
    emit('update:modelValue', index);
  } else {
    emit('update:modelValue', null);
    await delay(300);
    emit('update:modelValue', index);
  }
}

function highlightSlice(index: number, total: number, offsetY = 0) {
  if (index === props.modelValue) {
    let a = ((index + 0.5) / total) * Math.PI * 2;
    return `translate3d(${Math.sin(a) * 5}px, ${-Math.cos(a) * 5 + offsetY}px, 0) scale(1.1)`;
  }

  return `translate3d(0, ${offsetY}px, 0)`;
}

function getLabelRadius() {
  if (window.innerWidth < 400) return 25; // Small screens (iPhone Mini, SE, old Samsung)
  if (window.innerWidth < 768) return 30; // Medium screens (iPhone, small Android)
  if (window.innerWidth < 1024) return 35; // Tablets, Foldables
  return 40; // Large screens (Desktops, iPhone Max, Samsung Ultra)
}

function labelX(index: number): string {
  const segmentSize = (Math.PI * 2) / props.segments;
  const halfSegment = segmentSize / 2;
  const angle = index * segmentSize + halfSegment;
  const labelRadius = getLabelRadius(); // Dynamic radius
  return 50 + Math.sin(angle) * labelRadius + '%';
}

function labelY(index: number): string {
  const segmentSize = (Math.PI * 2) / props.segments;
  const halfSegment = segmentSize / 2;
  const angle = index * segmentSize + halfSegment;
  const labelRadius = getLabelRadius();
  return 50 - Math.cos(angle) * labelRadius + '%';
}
</script>
<template>
  <div class="relative w-full flex items-center justify-center">
    <!-- Pie Chart -->
    <svg
      class="w-full h-auto aspect-square"
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid meet"
    >
      <!-- Background Circle -->
      <circle cx="50" cy="50" r="37" class="fill-gray-200" />

      <!-- Segments -->
      <template v-for="index in props.segments">
        <path
          :d="circleSegment(index - 1, props.segments)"
          :class="[props.colors[index - 1], 'transition origin-center']"
          :style="{ transform: highlightSlice(index - 1, props.segments) }"
          @click="onSliceClick(index - 1)"
        />
      </template>

      <!-- Borders -->
      <template v-for="index in props.segments">
        <path
          :d="circleSegment(index - 1, props.segments)"
          class="stroke-[0.15] stroke-slate-900 fill-transparent pointer-events-none transition origin-center"
        />
      </template>
    </svg>

    <!-- Center Number -->
    <div class="absolute text-[clamp(16px,5vw,32px)] font-bold text-center">
      {{
        props.modelValue !== null ? props.labels?.[props.modelValue] : 'Välj'
      }}
    </div>

    <!-- Labels in Each Segment -->
    <template v-if="props.labels !== undefined">
      <template v-for="index in props.segments">
        <div
          class="absolute text-[clamp(10px,3vw,18px)] font-bold -translate-x-1/2 -translate-y-1/2 transition-all pointer-events-none"
          :style="{ left: labelX(index - 1), top: labelY(index - 1) }"
        >
          {{ props.labels[index - 1] }}
        </div>
      </template>
    </template>
  </div>
</template>

<style scoped>
svg {
  max-width: 100%;
  height: auto;
  display: block;
}

@media (max-width: 400px) {
  div.absolute {
    font-size: 12px; /* Smaller text on tiny screens */
  }
}

@media (min-width: 768px) {
  div.absolute {
    font-size: 16px; /* Larger text on tablets and above */
  }
}
</style>
